import {
  INSUREIQ_ENDPOINTS,
  InsureIQApiClient,
  InsureIQApiClientAuthMethods,
  InsureIQApiClientOptions,
} from '@harvestiq/insureiq-api-client';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from '../components/Loading/Loading';
import { insureiqPages } from '@harvestiq/constants';
import { useRedirect } from './Redirect/index';

type InsureIQApiClientContextProps = {
  insureIQApiClient: InsureIQApiClient;
};

export const InsureIQApiClientContext =
  createContext<InsureIQApiClientContextProps | null>(null);

export function InsureIQApiClientProvider({ children }: PropsWithChildren) {
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout } =
    useAuth0();
  const { setRedirect } = useRedirect();

  const authMethod = InsureIQApiClientAuthMethods.auth0AccessToken;

  const getAuth0AccessToken = () => {
    // Only attempt to get the access token if the user is authenticated
    if (isAuthenticated) {
      return getAccessTokenSilently();
    }
    // Otherwise make API calls without an access token
    return Promise.resolve(undefined);
  };

  // Handle 401s from the API
  const onUnauthorized: InsureIQApiClientOptions['onUnauthorized'] = async ({
    method,
    endpoint,
  }) => {
    // User context handles 401s for /users/me separately
    // See: user-context.tsx
    if (method === 'get' && endpoint === `${INSUREIQ_ENDPOINTS.users}/me`) {
      console.log('User context handles 401 for /users/me');
      return;
    }

    // Save user's current path to return them to it after login
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    setRedirect(currentPath);

    const signInMessage = encodeURIComponent(
      'Your session has expired. Please sign in again.'
    );

    await logout({
      logoutParams: {
        returnTo: `${window.location.origin}${insureiqPages.signIn.path}?msg=${signInMessage}`,
      },
    });
    return;
  };

  const insureIQApiClient = new InsureIQApiClient({
    authMethod,
    getAuth0AccessToken,
    onUnauthorized,
  });

  const contextValue: InsureIQApiClientContextProps = {
    insureIQApiClient,
  };

  return (
    <InsureIQApiClientContext.Provider value={contextValue}>
      {isLoading ? <Loading /> : children}
    </InsureIQApiClientContext.Provider>
  );
}

export function useInsureIQApiClient() {
  const context = useContext(InsureIQApiClientContext);
  if (!context) {
    throw new Error(
      'useInsureIQApiClient must be used within an InsureIQApiClientProvider'
    );
  }
  return context;
}
